import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { getDripQuestions, getDripComments } from '../utils/firestoreApi';
import DripVideoWatch from './DripVideoWatch';
import DripVideoSelect from './DripVideoSelect';
import DripSubmit from './DripSubmit';

const DripContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  color: white;
  box-sizing: border-box;
  height: 100%;
  backdrop-filter: ${props => props.isVideoComplete ? 'blur(10px)' : 'none'};
  border-radius: 25px;
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  font-size: 1.1rem;
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 10px;
`;

// 게임 상태 정의
const GAME_STATES = {
  VIDEO: 'video',
  SELECTION: 'selection',
  DRIP: 'drip',
  VIDEO_WATCH: 'video_watch'
};

function DripBattle({ onComplete }) {
  const [gameState, setGameState] = useState(GAME_STATES.SELECTION);
  const [dripData, setDripData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoComplete, setIsVideoComplete] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [userDrip, setUserDrip] = useState('');
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // 드립 데이터 로드
  useEffect(() => {
    const loadDripData = async () => {
      try {
        setLoading(true);
        const data = await getDripQuestions();
        if (data) {
          setDripData(data);
        } else {
          setError('드립 데이터를 찾을 수 없습니다.');
        }
      } catch (err) {
        setError('드립 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDripData();
  }, []);

  const currentVideo = dripData[currentVideoIndex];

  // 영상 완료 핸들러
  const handleVideoEnd = () => {
    setIsVideoComplete(true);
    
    // 다음 영상이 있으면 다음 영상으로, 없으면 선택 화면으로
    if (currentVideoIndex < dripData.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    } else {
      setGameState(GAME_STATES.SELECTION);
    }
    
    setIsVideoComplete(false);
  };

  // 영상 선택 핸들러
  const handleVideoSelect = (videoIndex) => {
    const selectedVideoData = dripData[videoIndex];
    setSelectedVideo(selectedVideoData);
    setCurrentVideoIndex(videoIndex);
    setGameState(GAME_STATES.VIDEO_WATCH);
    // 선택한 영상의 댓글을 바로 로드
    if (selectedVideoData && selectedVideoData.videoId) {
      loadComments(selectedVideoData.videoId);
    }
  };

  // 뒤로가기 핸들러
  const handleBack = () => {
    setGameState(GAME_STATES.SELECTION);
    setSelectedVideo(null);
    setComments([]);
  };

  // 댓글 데이터 로드 함수
  const loadComments = async (videoId) => {
    if (!videoId) return;
    
    try {
      setCommentsLoading(true);
      const commentsData = await getDripComments(videoId);
      if (commentsData && commentsData.length > 0) {
        setComments(commentsData);
      } else {
        setComments([]);
      }
    } catch (error) {
      //console.error('댓글 로드 실패:', error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  // 영상이 완료되면 댓글 로드 (이제 사용하지 않음)
  // useEffect(() => {
  //   if (isVideoComplete && selectedVideo && selectedVideo.videoId) {
  //     loadComments(selectedVideo.videoId);
  //   }
  // }, [isVideoComplete, selectedVideo]);

  // 드립 제출 핸들러 (새로운 로직)
  const handleSubmit = (result) => {
    // 결과 전달 (선택한 영상 ID와 드립 포함)
    const finalResult = {
      videoId: selectedVideo.videoId,
      title: selectedVideo.title,
      timestamp: new Date().toISOString()
    };
    
    onComplete && onComplete(finalResult);
  };

  // 에러 처리
  if (error) {
    return (
      <DripContainer>
        <ErrorText>{error}</ErrorText>
      </DripContainer>
    );
  }

  // 로딩 처리
  if (loading) {
    return (
      <DripContainer>
        <div style={{ color: 'white', textAlign: 'center' }}>드립 영상을 불러오는 중...</div>
      </DripContainer>
    );
  }

  // 드립 데이터가 없으면 처리
  if (!dripData.length) {
    return (
      <DripContainer>
        <ErrorText>드립 영상 데이터를 불러올 수 없습니다.</ErrorText>
      </DripContainer>
    );
  }

  return (
    <DripContainer isVideoComplete={isVideoComplete}>
      <AnimatePresence mode="wait">
        {/* 영상 시청 단계 */}
        {/* {gameState === GAME_STATES.VIDEO && currentVideo && (
          <DripVideoWatch
            currentVideo={currentVideo}
            currentVideoIndex={currentVideoIndex}
            totalVideos={dripData.length}
            onVideoEnd={handleVideoEnd}
          />
        )} */}

        {/* 영상 선택 화면 */}
        {gameState === GAME_STATES.SELECTION && (
          <DripVideoSelect
            dripData={dripData}
            onVideoSelect={handleVideoSelect}
          />
        )}

        {/* 영상 시청 및 댓글 화면 */}
        {gameState === GAME_STATES.VIDEO_WATCH && selectedVideo && (
          <DripVideoWatch
            currentVideo={selectedVideo}
            currentVideoIndex={currentVideoIndex}
            totalVideos={dripData.length}
            onVideoEnd={handleVideoEnd}
            comments={comments}
            commentsLoading={commentsLoading}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        )}

        {/* 드립 작성 화면 */}
        {gameState === GAME_STATES.DRIP && selectedVideo && (
          <DripSubmit
            selectedVideo={selectedVideo}
            comments={comments}
            commentsLoading={commentsLoading}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        )}
      </AnimatePresence>
    </DripContainer>
  );
}

export default DripBattle; 